<template>
    <div class="tw-grid tw-grid-cols-12 tw-gap-4">
        <!-- Main Content - Calendar/List View -->
        <div class="tw-col-span-12 lg:tw-col-span-9">
            <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200 tw-min-h-[600px]">
                <!-- Header Section -->
                <div class="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-4 tw-mb-6">
                    <div>
                        <h1 v-if="!loading" class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-1">Meetings</h1>
                        <div v-else class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-32 tw-mb-1 tw-animate-pulse"></div>
                        
                        <p v-if="!loading" class="tw-text-sm tw-text-gray-600">{{ meetings.length }} meetings total</p>
                        <div v-else class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse"></div>
                    </div>
                    
                    <!-- Header Actions -->
                    <div v-if="!loading" class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                        <!-- Search -->
                        <div class="tw-min-w-[250px]">
                            <FormField
                                v-model="searchQuery"
                                type="text"
                                placeholder="Search meetings..."
                                left-icon="solar:magnifer-linear"
                                hide-label
                                hide-errors
                                class="tw-w-full"
                            />
                        </div>
                        
                        <!-- View Toggle -->
                        <div class="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-p-1">
                            <button
                                @click="viewMode = 'calendar'"
                                :class="[
                                    'tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-transition-colors',
                                    viewMode === 'calendar' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-text-gray-600 hover:tw-text-gray-900'
                                ]"
                            >
                                <icon icon="solar:calendar-bold-duotone" class="tw-text-lg" />
                            </button>
                            <button
                                @click="viewMode = 'list'"
                                :class="[
                                    'tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-transition-colors',
                                    viewMode === 'list' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-text-gray-600 hover:tw-text-gray-900'
                                ]"
                            >
                                <icon icon="solar:list-bold-duotone" class="tw-text-lg" />
                            </button>
                        </div>
                        
                        <!-- Schedule Meeting Button -->
                        <a-button
                            @click="scheduleNewMeeting"
                            intent="gradient"
                            left-icon="solar:add-circle-bold-duotone"
                        >
                            Schedule Meeting
                        </a-button>
                        
                    </div>
                    
                    <!-- Header Actions Skeleton -->
                    <div v-else class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-md tw-w-64 tw-animate-pulse"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-md tw-w-20 tw-animate-pulse"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-md tw-w-32 tw-animate-pulse"></div>
                    </div>
                </div>

                <!-- Filters Bar -->
                <div v-if="false" class="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-mb-6 tw-p-4 tw-bg-gray-50 tw-rounded-lg">
                    <!-- Date Range Filter -->
                    <FormField
                        v-model="dateFilter"
                        type="select"
                        hide-label
                        hide-errors
                        left-icon="solar:calendar-linear"
                        class="tw-min-w-[150px]"
                    >
                        <option value="all">All Dates</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="upcoming">Upcoming</option>
                    </FormField>

                    <!-- Status Filter -->
                    <FormField
                        v-model="statusFilter"
                        type="select"
                        hide-label
                        hide-errors
                        left-icon="solar:flag-linear"
                        class="tw-min-w-[150px]"
                    >
                        <option value="all">All Status</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="ongoing">Ongoing</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                    </FormField>

                    <!-- Meeting Type Filter -->
                    <FormField
                        v-model="typeFilter"
                        type="select"
                        hide-label
                        hide-errors
                        left-icon="solar:video-camera-linear"
                        class="tw-min-w-[150px]"
                    >
                        <option value="all">All Types</option>
                        <option value="video">Video Call</option>
                        <option value="in-person">In-Person</option>
                        <option value="phone">Phone Call</option>
                    </FormField>

                    <!-- Clear Filters -->
                    <a-button
                        v-if="hasActiveFilters"
                        @click="clearFilters"
                        intent="ghost"
                        left-icon="solar:refresh-bold-duotone"
                        class="tw-text-sm"
                    >
                        Clear
                    </a-button>
                </div>

                <!-- Content Area -->
                <div class="tw-flex-1">
                    <!-- Loading State -->
                    <div v-if="loading">
                        <!-- Calendar View Skeleton -->
                        <div v-if="viewMode === 'calendar'" class="tw-space-y-4">
                            <!-- Calendar Header Skeleton -->
                            <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
                                <div class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-48 tw-animate-pulse"></div>
                                <div class="tw-flex tw-gap-2">
                                    <div class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-20 tw-animate-pulse"></div>
                                    <div class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-20 tw-animate-pulse"></div>
                                </div>
                            </div>
                            
                            <!-- Calendar Grid Skeleton -->
                            <div class="tw-grid tw-grid-cols-7 tw-gap-1 tw-border tw-border-gray-200 tw-rounded-lg tw-p-4">
                                <!-- Day Headers -->
                                <div v-for="n in 7" :key="n" class="tw-p-2 tw-text-center">
                                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                </div>
                                <!-- Calendar Days -->
                                <div v-for="n in 35" :key="n" class="tw-p-2 tw-h-24 tw-border tw-border-gray-100">
                                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-6 tw-mb-2 tw-animate-pulse"></div>
                                    <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-full tw-animate-pulse"></div>
                                </div>
                            </div>
                        </div>

                        <!-- List View Skeleton -->
                        <div v-else class="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                            <table class="tw-min-w-full tw-divide-y tw-divide-gray-200 tw-table-fixed">
                                <thead class="tw-bg-gray-50">
                                    <tr>
                                        <th class="tw-w-[35%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Meeting</th>
                                        <th class="tw-w-[18%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Date & Time</th>
                                        <th class="tw-w-[15%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Participants</th>
                                        <th class="tw-w-[12%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Type</th>
                                        <th class="tw-w-[10%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Status</th>
                                        <th class="tw-w-[10%] tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
                                    <tr v-for="n in 8" :key="n" class="tw-animate-pulse">
                                        <td class="tw-px-3 tw-py-2">
                                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32"></div>
                                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-24 tw-mt-1"></div>
                                        </td>
                                        <td class="tw-px-3 tw-py-2">
                                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-20"></div>
                                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-16 tw-mt-1"></div>
                                        </td>
                                        <td class="tw-px-3 tw-py-2">
                                            <div class="tw-flex tw-gap-1">
                                                <div class="tw-w-5 tw-h-5 tw-bg-gray-200 tw-rounded-full"></div>
                                                <div class="tw-w-5 tw-h-5 tw-bg-gray-200 tw-rounded-full"></div>
                                            </div>
                                        </td>
                                        <td class="tw-px-3 tw-py-2">
                                            <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-14"></div>
                                        </td>
                                        <td class="tw-px-3 tw-py-2">
                                            <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-16"></div>
                                        </td>
                                        <td class="tw-px-3 tw-py-2 tw-text-right">
                                            <div class="tw-flex tw-items-center tw-justify-end tw-gap-1">
                                                <div class="tw-w-5 tw-h-5 tw-bg-gray-200 tw-rounded"></div>
                                                <div class="tw-w-5 tw-h-5 tw-bg-gray-200 tw-rounded"></div>
                                                <div class="tw-w-5 tw-h-5 tw-bg-gray-200 tw-rounded"></div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Empty State -->
                    <div v-else-if="filteredMeetings.length === 0 && !searchQuery && !hasActiveFilters" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center">
                        <icon icon="solar:calendar-add-linear" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">No meetings scheduled</h3>
                        <p class="tw-text-gray-600 tw-mb-4">Start by scheduling your first meeting</p>
                        <a-button
                            @click="scheduleNewMeeting"
                            intent="primary"
                            left-icon="solar:add-circle-bold-duotone"
                        >
                            Schedule Meeting
                        </a-button>
                    </div>

                    <!-- No Search Results -->
                    <div v-else-if="filteredMeetings.length === 0" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center">
                        <icon icon="solar:calendar-search-bold-duotone" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">No meetings found</h3>
                        <p class="tw-text-gray-600 tw-mb-4">Try adjusting your search or filters</p>
                        <a-button
                            @click="clearFilters"
                            intent="secondary"
                            left-icon="solar:refresh-bold-duotone"
                        >
                            Clear Filters
                        </a-button>
                    </div>

                    <!-- Calendar View -->
                    <div v-else-if="viewMode === 'calendar'" class="tw-space-y-4">
                        <!-- Calendar Header -->
                        <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
                            <h2 class="tw-text-xl tw-font-semibold tw-text-gray-900">{{ currentMonthYear }}</h2>
                            <div class="tw-flex tw-items-center tw-gap-2">
                                <a-button
                                    @click="previousMonth"
                                    intent="light"
                                    left-icon="solar:arrow-left-linear"
                                    class="tw-text-sm"
                                >
                                    Previous
                                </a-button>
                                <a-button
                                    @click="goToToday"
                                    intent="light"
                                    class="tw-text-sm"
                                >
                                    Today
                                </a-button>
                                <a-button
                                    @click="nextMonth"
                                    intent="light"
                                    right-icon="solar:arrow-right-linear"
                                    class="tw-text-sm"
                                >
                                    Next
                                </a-button>
                            </div>
                        </div>

                        <!-- Calendar Grid -->
                        <div class="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
                            <!-- Day Headers -->
                            <div class="tw-grid tw-grid-cols-7 tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                <div
                                    v-for="day in dayHeaders"
                                    :key="day"
                                    class="tw-p-3 tw-text-center tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider"
                                >
                                    {{ day }}
                                </div>
                            </div>
                            
                            <!-- Calendar Days -->
                            <div class="tw-grid tw-grid-cols-7">
                                <div
                                    v-for="(day, index) in calendarDays"
                                    :key="index"
                                    :class="[
                                        'tw-min-h-[120px] tw-p-2 tw-border-r tw-border-b tw-border-gray-100',
                                        day.isCurrentMonth ? 'tw-bg-white' : 'tw-bg-gray-50',
                                        day.isToday ? 'tw-bg-blue-50' : ''
                                    ]"
                                >
                                                    <!-- Day Number -->
                <div :class="[
                  'tw-text-sm tw-font-medium tw-mb-1',
                  day.isCurrentMonth ? 'tw-text-gray-900' : 'tw-text-gray-400',
                  day.isToday ? 'tw-w-6 tw-h-6 tw-bg-blue-600 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-font-bold' : ''
                ]">
                  {{ day.date }}
                </div>
                                    
                                    <!-- Meetings for this day -->
                                    <div class="tw-space-y-1">
                                        <div
                                            v-for="meeting in day.meetings.slice(0, 2)"
                                            :key="meeting.id"
                                            @click="viewMeeting(meeting)"
                                            :class="[
                                                'tw-text-xs tw-p-1 tw-rounded tw-cursor-pointer tw-truncate',
                                                getStatusColor(meeting.status)
                                            ]"
                                            :title="meeting.title"
                                        >
                                            {{ meeting.time }} - {{ meeting.title }}
                                        </div>
                                        
                                        <!-- More meetings indicator -->
                                        <div
                                            v-if="day.meetings.length > 2"
                                            class="tw-text-xs tw-text-gray-500 tw-cursor-pointer hover:tw-text-gray-700"
                                            @click="viewDayMeetings(day)"
                                        >
                                            +{{ day.meetings.length - 2 }} more
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- List View -->
                    <div v-else class="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                        <table class="tw-min-w-full tw-divide-y tw-divide-gray-200 tw-table-fixed">
                            <thead class="tw-bg-gray-50">
                                <tr>
                                    <th class="tw-w-[35%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Meeting</th>
                                    <th class="tw-w-[18%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Date & Time</th>
                                    <th class="tw-w-[15%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Participants</th>
                                    <th class="tw-w-[12%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Type</th>
                                    <th class="tw-w-[10%] tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Status</th>
                                    <th class="tw-w-[10%] tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
                                <tr
                                    v-for="meeting in paginatedMeetings"
                                    :key="meeting.id"
                                    class="hover:tw-bg-gray-50"
                                >
                                    <td class="tw-px-3 tw-py-2">
                                        <div>
                                            <div class="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">{{ meeting.title }}</div>
                                            <div v-if="meeting.description" class="tw-text-xs tw-text-gray-500 tw-truncate">{{ meeting.description }}</div>
                                        </div>
                                    </td>
                                    <td class="tw-px-3 tw-py-2">
                                        <div class="tw-text-xs tw-text-gray-900 tw-whitespace-nowrap">{{ formatCompactDate(meeting.date) }}</div>
                                        <div class="tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">{{ meeting.time }}-{{ meeting.endTime }}</div>
                                    </td>
                                    <td class="tw-px-3 tw-py-2">
                                        <div class="tw-flex tw-items-center tw-gap-1">
                                            <div
                                                v-for="participant in meeting.participants.slice(0, 2)"
                                                :key="participant.id"
                                                class="tw-w-5 tw-h-5 tw-rounded-full tw-bg-gray-300 tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-medium tw-text-gray-700"
                                                :title="participant.name"
                                            >
                                                {{ participant.name.charAt(0).toUpperCase() }}
                                            </div>
                                            <span
                                                v-if="meeting.participants.length > 2"
                                                class="tw-text-xs tw-text-gray-500 tw-ml-1"
                                            >
                                                +{{ meeting.participants.length - 2 }}
                                            </span>
                                        </div>
                                    </td>
                                    <td class="tw-px-3 tw-py-2">
                                        <span :class="[
                                            'tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium',
                                            getTypeColor(meeting.type)
                                        ]">
                                            <icon :icon="getTypeIcon(meeting.type)" class="tw-mr-1 tw-text-xs" />
                                            <span class="tw-truncate">{{ meeting.type }}</span>
                                        </span>
                                    </td>
                                    <td class="tw-px-3 tw-py-2">
                                        <span :class="[
                                            'tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium',
                                            getStatusColor(meeting.status)
                                        ]">
                                            {{ meeting.status }}
                                        </span>
                                    </td>
                                    <td class="tw-px-3 tw-py-2 tw-text-right">
                                        <div class="tw-flex tw-items-center tw-justify-end tw-gap-1">
                                            <button
                                                @click="viewMeeting(meeting)"
                                                class="tw-text-gray-400 hover:tw-text-gray-600"
                                                title="View"
                                            >
                                                <icon icon="solar:eye-bold-duotone" class="tw-text-sm" />
                                            </button>
                                            <button
                                                v-if="meeting.status === 'scheduled'"
                                                @click="joinMeeting(meeting)"
                                                class="tw-text-gray-400 hover:tw-text-green-600"
                                                title="Join"
                                            >
                                                <icon icon="solar:video-camera-bold-duotone" class="tw-text-sm" />
                                            </button>
                                            <button
                                                @click="editMeeting(meeting)"
                                                class="tw-text-gray-400 hover:tw-text-gray-600"
                                                title="Edit"
                                            >
                                                <icon icon="solar:pen-bold-duotone" class="tw-text-sm" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="viewMode === 'list' && totalPages > 1" class="tw-flex tw-items-center tw-justify-between tw-mt-6">
                        <div class="tw-text-sm tw-text-gray-600">
                            Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} to {{ Math.min(currentPage * itemsPerPage, filteredMeetings.length) }} of {{ filteredMeetings.length }} meetings
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <a-button
                                @click="currentPage--"
                                :disabled="currentPage === 1"
                                intent="light"
                                left-icon="solar:arrow-left-linear"
                            >
                                Previous
                            </a-button>
                            <a-button
                                @click="currentPage++"
                                :disabled="currentPage === totalPages"
                                intent="light"
                                right-icon="solar:arrow-right-linear"
                            >
                                Next
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Right Sidebar - Stats -->
        <div class="tw-col-span-12 lg:tw-col-span-3">
            <div class="tw-space-y-3">
                <!-- Your Stats Card -->
                <div class="tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200">
                    <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
                        <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">Your stats</h3>
                        <icon icon="solar:settings-linear" class="tw-text-sm tw-text-gray-400" />
                    </div>
                    
                    <div class="tw-space-y-2">
                        <!-- Scheduled Meetings -->
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <div class="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-green-100 tw-flex tw-items-center tw-justify-center">
                                <icon icon="solar:calendar-add-bold" class="tw-text-sm tw-text-green-600" />
                            </div>
                            <div>
                                <div class="tw-text-sm tw-font-medium tw-text-gray-900">{{ scheduledCount }} Scheduled meetings</div>
                            </div>
                        </div>
                        
                        <!-- Completed Meetings -->
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <div class="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-green-100 tw-flex tw-items-center tw-justify-center">
                                <icon icon="solar:calendar-mark-bold" class="tw-text-sm tw-text-green-600" />
                            </div>
                            <div>
                                <div class="tw-text-sm tw-font-medium tw-text-gray-900">{{ completedCount }} Completed meetings</div>
                            </div>
                        </div>
                        
                        <!-- Ongoing Meetings -->
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <div class="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-orange-100 tw-flex tw-items-center tw-justify-center">
                                <icon icon="solar:calendar-bold" class="tw-text-sm tw-text-orange-600" />
                            </div>
                            <div>
                                <div class="tw-text-sm tw-font-medium tw-text-gray-900">{{ ongoingCount }} Ongoing meetings</div>
                            </div>
                        </div>
                        
                        <!-- Cancelled Meetings -->
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <div class="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-red-100 tw-flex tw-items-center tw-justify-center">
                                <icon icon="solar:close-circle-bold" class="tw-text-sm tw-text-red-600" />
                            </div>
                            <div>
                                <div class="tw-text-sm tw-font-medium tw-text-gray-900">{{ cancelledCount }} Cancelled meetings</div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Export Card -->
                <div class="tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200">
                    <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-1">Download your meetings summary</h3>
                    <p class="tw-text-xs tw-text-gray-600 tw-mb-3">Get all meetings in an excel file</p>
                    
                    <a-button
                        @click="exportMeetings"
                        intent="secondary"
                        left-icon="solar:download-bold"
                        class="tw-w-full tw-justify-start tw-text-sm tw-py-2"
                    >
                        Export meetings
                    </a-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import FormField from '@/components/form/FormField.vue'
import useAlert from '@/composables/useAlert'

// Data
const loading = ref(false)
const searchQuery = ref('')
const viewMode = ref('calendar')
const currentPage = ref(1)
const itemsPerPage = ref(10)

// Filters
const dateFilter = ref('all')
const statusFilter = ref('all')
const typeFilter = ref('all')

// Calendar state
const currentDate = ref(new Date())

// Sample meetings data
const meetings = ref([
    {
        id: 1,
        title: 'Project Kickoff Meeting',
        description: 'Initial meeting to discuss project scope and timeline',
        date: new Date(2025, 5, 15), // June 15, 2025
        time: '09:00',
        endTime: '10:00',
        type: 'Video Call',
        status: 'scheduled',
        participants: [
            { id: 1, name: 'John Smith' },
            { id: 2, name: 'Sarah Johnson' },
            { id: 3, name: 'Mike Davis' }
        ]
    },
    {
        id: 2,
        title: 'Client Presentation',
        description: 'Present the final design concepts to the client',
        date: new Date(2025, 5, 16), // June 16, 2025
        time: '14:00',
        endTime: '15:30',
        type: 'In-Person',
        status: 'scheduled',
        participants: [
            { id: 1, name: 'John Smith' },
            { id: 4, name: 'Emily Chen' }
        ]
    },
    {
        id: 3,
        title: 'Weekly Standup',
        description: 'Team sync and progress updates',
        date: new Date(2025, 5, 17), // June 17, 2025
        time: '10:00',
        endTime: '10:30',
        type: 'Video Call',
        status: 'ongoing',
        participants: [
            { id: 1, name: 'John Smith' },
            { id: 2, name: 'Sarah Johnson' },
            { id: 3, name: 'Mike Davis' },
            { id: 5, name: 'Lisa Wang' }
        ]
    },
    {
        id: 4,
        title: 'Budget Review',
        description: 'Quarterly budget review and planning',
        date: new Date(2025, 5, 12), // June 12, 2025
        time: '11:00',
        endTime: '12:00',
        type: 'Phone Call',
        status: 'completed',
        participants: [
            { id: 1, name: 'John Smith' },
            { id: 6, name: 'Robert Brown' }
        ]
    },
    {
        id: 5,
        title: 'Training Session',
        description: 'New employee onboarding and training',
        date: new Date(2025, 5, 20), // June 20, 2025
        time: '13:00',
        endTime: '16:00',
        type: 'In-Person',
        status: 'scheduled',
        participants: [
            { id: 2, name: 'Sarah Johnson' },
            { id: 7, name: 'Alex Turner' },
            { id: 8, name: 'Maria Garcia' }
        ]
    },
    {
        id: 6,
        title: 'Code Review',
        description: 'Review latest feature implementation',
        date: new Date(2025, 5, 18), // June 18, 2025
        time: '15:00',
        endTime: '16:00',
        type: 'Video Call',
        status: 'cancelled',
        participants: [
            { id: 3, name: 'Mike Davis' },
            { id: 5, name: 'Lisa Wang' }
        ]
    },
    {
        id: 7,
        title: 'Sprint Planning',
        description: 'Plan tasks for the upcoming sprint',
        date: new Date(2025, 5, 10), // June 10, 2025
        time: '09:00',
        endTime: '11:00',
        type: 'Video Call',
        status: 'completed',
        participants: [
            { id: 1, name: 'John Smith' },
            { id: 2, name: 'Sarah Johnson' },
            { id: 3, name: 'Mike Davis' }
        ]
    },
    {
        id: 8,
        title: 'Design Review',
        description: 'Review new UI/UX designs',
        date: new Date(2025, 5, 8), // June 8, 2025
        time: '15:00',
        endTime: '16:30',
        type: 'Video Call',
        status: 'completed',
        participants: [
            { id: 4, name: 'Emily Wilson' },
            { id: 5, name: 'Lisa Wang' }
        ]
    },
    {
        id: 9,
        title: 'Team Retrospective',
        description: 'Weekly retrospective meeting',
        date: new Date(2025, 5, 6), // June 6, 2025
        time: '14:00',
        endTime: '15:00',
        type: 'Video Call',
        status: 'completed',
        participants: [
            { id: 1, name: 'John Smith' },
            { id: 2, name: 'Sarah Johnson' },
            { id: 3, name: 'Mike Davis' },
            { id: 4, name: 'Emily Wilson' }
        ]
    }
])

// Computed properties
const hasActiveFilters = computed(() => {
    return dateFilter.value !== 'all' || statusFilter.value !== 'all' || typeFilter.value !== 'all' || searchQuery.value
})

const filteredMeetings = computed(() => {
    let filtered = meetings.value

    // Search filter
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(meeting =>
            meeting.title.toLowerCase().includes(query) ||
            meeting.description?.toLowerCase().includes(query) ||
            meeting.participants.some(p => p.name.toLowerCase().includes(query))
        )
    }

    // Date filter
    if (dateFilter.value !== 'all') {
        const today = new Date()
        const startOfWeek = new Date(today)
        startOfWeek.setDate(today.getDate() - today.getDay())
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

        filtered = filtered.filter(meeting => {
            const meetingDate = meeting.date
            switch (dateFilter.value) {
                case 'today':
                    return meetingDate.toDateString() === today.toDateString()
                case 'week':
                    return meetingDate >= startOfWeek && meetingDate <= new Date(startOfWeek.getTime() + 7 * 24 * 60 * 60 * 1000)
                case 'month':
                    return meetingDate >= startOfMonth && meetingDate < new Date(today.getFullYear(), today.getMonth() + 1, 1)
                case 'upcoming':
                    return meetingDate >= today
                default:
                    return true
            }
        })
    }

    // Status filter
    if (statusFilter.value !== 'all') {
        filtered = filtered.filter(meeting => meeting.status === statusFilter.value)
    }

    // Type filter
    if (typeFilter.value !== 'all') {
        filtered = filtered.filter(meeting => meeting.type.toLowerCase().replace(' ', '-') === typeFilter.value)
    }

    return filtered
})

const paginatedMeetings = computed(() => {
    if (viewMode.value !== 'list') return filteredMeetings.value
    
    const start = (currentPage.value - 1) * itemsPerPage.value
    const end = start + itemsPerPage.value
    return filteredMeetings.value.slice(start, end)
})

const totalPages = computed(() => {
    return Math.ceil(filteredMeetings.value.length / itemsPerPage.value)
})

// Stats computed properties
const scheduledCount = computed(() => {
    return meetings.value.filter(meeting => meeting.status === 'scheduled').length
})

const completedCount = computed(() => {
    return meetings.value.filter(meeting => meeting.status === 'completed').length
})

const ongoingCount = computed(() => {
    return meetings.value.filter(meeting => meeting.status === 'ongoing').length
})

const cancelledCount = computed(() => {
    return meetings.value.filter(meeting => meeting.status === 'cancelled').length
})

// Calendar computed properties
const currentMonthYear = computed(() => {
    return currentDate.value.toLocaleDateString('en-US', { 
        month: 'long', 
        year: 'numeric' 
    })
})

const dayHeaders = computed(() => {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
})

const calendarDays = computed(() => {
    const year = currentDate.value.getFullYear()
    const month = currentDate.value.getMonth()
    const firstDay = new Date(year, month, 1)
    const startDate = new Date(firstDay)
    startDate.setDate(startDate.getDate() - firstDay.getDay())
    
    const days = []
    const today = new Date()
    
    for (let i = 0; i < 42; i++) {
        const date = new Date(startDate)
        date.setDate(startDate.getDate() + i)
        
        const dayMeetings = filteredMeetings.value.filter(meeting => 
            meeting.date.toDateString() === date.toDateString()
        )
        
        days.push({
            date: date.getDate(),
            fullDate: date,
            isCurrentMonth: date.getMonth() === month,
            isToday: date.toDateString() === today.toDateString(),
            meetings: dayMeetings
        })
    }
    
    return days
})

// Methods
const clearFilters = () => {
    dateFilter.value = 'all'
    statusFilter.value = 'all'
    typeFilter.value = 'all'
    searchQuery.value = ''
    currentPage.value = 1
}

const scheduleNewMeeting = () => {
    console.log('Schedule new meeting')
    useAlert('Meeting scheduling feature coming soon!', 'info')
}

const viewMeeting = (meeting) => {
    console.log('Viewing meeting:', meeting.title)
    useAlert(`Viewing: ${meeting.title}`, 'info')
}

const editMeeting = (meeting) => {
    console.log('Editing meeting:', meeting.title)
    useAlert(`Editing: ${meeting.title}`, 'info')
}

const joinMeeting = (meeting) => {
    console.log('Joining meeting:', meeting.title)
    useAlert(`Joining: ${meeting.title}`, 'success')
}



const viewDayMeetings = (day) => {
    console.log('View meetings for day:', day.fullDate)
    useAlert(`Viewing ${day.meetings.length} meetings for ${day.fullDate.toDateString()}`, 'info')
}

const exportMeetings = () => {
    console.log('Exporting meetings to Excel')
    useAlert('Meetings exported successfully!', 'success')
    // In a real app, this would generate and download an Excel file
}

// Calendar navigation
const previousMonth = () => {
    currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() - 1, 1)
}

const nextMonth = () => {
    currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 1)
}

const goToToday = () => {
    currentDate.value = new Date()
}

// Utility methods
const formatCompactDate = (date) => {
  try {
    // Check if the date is a string with timezone indicator
    if (typeof date === 'string') {
      const dateObj = new Date(date);
      const isUTC = date.includes('Z');
      
      if (isUTC) {
        // For UTC dates, use UTC methods to avoid timezone conversion
        const month = dateObj.toLocaleString('en-US', { month: 'short' });
        const day = dateObj.getUTCDate();
        return `${month} ${day}`;
      } else {
        // For local dates, use local methods
        return dateObj.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
      }
    } else {
      // If date is already a Date object, use local methods
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  } catch (error) {
    console.error('Error formatting compact date:', error);
    return 'Invalid Date';
  }
}

const getStatusColor = (status) => {
    switch (status) {
        case 'scheduled':
            return 'tw-bg-blue-100 tw-text-blue-800'
        case 'ongoing':
            return 'tw-bg-green-100 tw-text-green-800'
        case 'completed':
            return 'tw-bg-gray-100 tw-text-gray-800'
        case 'cancelled':
            return 'tw-bg-red-100 tw-text-red-800'
        default:
            return 'tw-bg-gray-100 tw-text-gray-800'
    }
}

const getTypeColor = (type) => {
    switch (type.toLowerCase().replace(' ', '-')) {
        case 'video-call':
            return 'tw-bg-purple-100 tw-text-purple-800'
        case 'in-person':
            return 'tw-bg-green-100 tw-text-green-800'
        case 'phone-call':
            return 'tw-bg-blue-100 tw-text-blue-800'
        default:
            return 'tw-bg-gray-100 tw-text-gray-800'
    }
}

const getTypeIcon = (type) => {
    switch (type.toLowerCase().replace(' ', '-')) {
        case 'video-call':
            return 'solar:video-camera-bold'
        case 'in-person':
            return 'solar:users-group-rounded-bold'
        case 'phone-call':
            return 'solar:phone-bold'
        default:
            return 'solar:calendar-bold'
    }
}

const loadData = async () => {
    loading.value = true

    try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Data is already loaded from the sample data
        
    } catch (error) {
        console.error('Error loading meetings:', error)
        useAlert('Failed to load meetings', 'danger')
    } finally {
        loading.value = false
    }
}

// Initialize
onMounted(() => {
    loadData()
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style> 